// Componentes básicos en 3D: LED, LED RGB, resistencia, botón, potenciómetros, interruptor, zumbador,
// capacitores, NE555 y placa táctil.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, glow, setGlow, cachedGeo } from '../kit.js';
import { LED_COLORS, BUTTON_COLORS, ceramicFarads, ceramicCode, electrolytic, PLATE } from '../../parts/basic.js';
import { PITCH, getPins } from '../../parts/registry.js';

const LEG_R = 0.24;

// LED ------------------------------------------------------------------------------------------------
function ledDome(colorHex, { diffused = false } = {}) {
  const c = new THREE.Color(colorHex);
  const base = c.clone().multiplyScalar(diffused ? 1 : 0.55);
  const material = new THREE.MeshStandardMaterial({
    color: diffused ? '#f4f4f0' : base,
    emissive: c,
    emissiveIntensity: 0,
    roughness: diffused ? 0.5 : 0.12,
    metalness: 0,
    transparent: true,
    opacity: diffused ? 0.94 : 0.82,
  });
  const g = new Builder();
  const domeGeo = cachedGeo('led-dome', () => {
    const pts = [];
    pts.push(new THREE.Vector2(0, 0));
    pts.push(new THREE.Vector2(2.9, 0));
    pts.push(new THREE.Vector2(2.9, 1.0));
    pts.push(new THREE.Vector2(2.5, 1.0));
    pts.push(new THREE.Vector2(2.5, 5.6));
    for (let i = 1; i <= 10; i++) {
      const a = (i / 10) * (Math.PI / 2);
      pts.push(new THREE.Vector2(2.5 * Math.cos(a), 5.6 + 2.5 * Math.sin(a)));
    }
    const geo = new THREE.LatheGeometry(pts, 28);
    geo.userData.shared = true;
    return geo;
  });
  const dome = new THREE.Mesh(domeGeo, material);
  dome.castShadow = true;
  return { dome, material, g };
}

export function buildLed(def, props) {
  const color = (LED_COLORS[props.color] || LED_COLORS.red).hex;
  const b = new Builder();
  const y0 = 3.2;
  // Cátodo (−) recto, ánodo (+) con codo: así se distingue la pata larga.
  b.leg([[-PITCH / 2, y0, 0], [-PITCH / 2, 0, 0]], LEG_R, M.tin);
  b.leg([[PITCH / 2, y0, 0], [PITCH / 2, 1.9, 0], [PITCH / 2 + 0.5, 1.3, 0], [PITCH / 2, 0.7, 0], [PITCH / 2, 0, 0]], LEG_R, M.tin);
  b.build();
  const { dome, material } = ledDome(color);
  dome.position.y = y0;
  b.root.add(dome);
  // Lado plano del cátodo
  const flat = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.0, 3.6), material);
  flat.position.set(-2.75, y0 + 0.5, 0);
  b.root.add(flat);
  const halo = glow(color, 22);
  halo.position.set(0, y0 + 5.6, 0);
  b.root.add(halo);
  return {
    root: b.root,
    update(v) {
      const k = v.b || 0;
      material.emissiveIntensity = k * 16;
      material.opacity = 0.82 + Math.min(0.18, k * 0.2);
      setGlow(halo, k);
    },
    reset() {
      this.update({ b: 0 });
    },
  };
}

export function buildRgbLed(def, props) {
  const b = new Builder();
  const y0 = 3.2;
  const xs = [-1.5 * PITCH, -0.5 * PITCH, 0.5 * PITCH, 1.5 * PITCH];
  xs.forEach((x, i) => {
    const top = [x * 0.35, y0, 0];
    b.leg([top, [x, y0 - 1.6, 0], [x, 0, 0]], LEG_R, M.tin);
    if (i === 1) b.leg([[x, 1.6, 0], [x + 0.5, 1.1, 0], [x, 0.6, 0]], LEG_R, M.tin);
  });
  b.build();
  const { dome, material } = ledDome('#ffffff', { diffused: true });
  dome.position.y = y0;
  b.root.add(dome);
  const halo = glow('#ffffff', 24);
  halo.position.set(0, y0 + 5.6, 0);
  b.root.add(halo);
  const col = new THREE.Color();
  return {
    root: b.root,
    update(v) {
      const [r, g, bl] = v.rgb || [0, 0, 0];
      const m = Math.max(r, g, bl);
      col.setRGB(Math.min(1, r), Math.min(1, g), Math.min(1, bl));
      material.emissive.copy(m > 0 ? col.clone().multiplyScalar(1 / Math.max(1e-6, Math.min(1, m))) : col);
      material.emissiveIntensity = m * 14;
      halo.material.color.copy(material.emissive);
      setGlow(halo, m);
    },
    reset() {
      this.update({ rgb: [0, 0, 0] });
    },
  };
}

// Resistencia -------------------------------------------------------------------------------------
const BAND = ['#151515', '#7a4a1f', '#d42b20', '#f07a12', '#f3d21b', '#2e9e3a', '#2858c8', '#7d3cb8', '#8a8a8a', '#f4f4f4'];
const GOLD = '#c9a33c';
const SILVER = '#b8bcc2';

export function resistorBands(ohms) {
  ohms = Math.max(0.1, Number(ohms) || 0);
  let exp = Math.floor(Math.log10(ohms)) - 1;
  let digits = Math.round(ohms / Math.pow(10, exp));
  if (digits >= 100) {
    digits = Math.round(digits / 10);
    exp += 1;
  }
  const d1 = Math.floor(digits / 10);
  const d2 = digits % 10;
  const mult = exp >= 0 ? BAND[Math.min(9, exp)] : exp === -1 ? GOLD : SILVER;
  return [BAND[d1], BAND[d2], mult, GOLD];
}

const bandTexCache = new Map();
function bandTexture(ohms) {
  const key = String(ohms);
  if (bandTexCache.has(key)) return bandTexCache.get(key);
  const c = document.createElement('canvas');
  c.width = 16;
  c.height = 128;
  const g = c.getContext('2d');
  g.fillStyle = '#d9c08e';
  g.fillRect(0, 0, 16, 128);
  const bands = resistorBands(ohms);
  // v crece a lo largo del eje: dibujamos las bandas a lo alto.
  const at = [0.14, 0.32, 0.5, 0.82];
  bands.forEach((color, i) => {
    g.fillStyle = color;
    g.fillRect(0, (1 - at[i]) * 128 - 7, 16, 14);
  });
  const t = texture(c, { aniso: 4 });
  t.userData.shared = true;
  bandTexCache.set(key, t);
  return t;
}

export function buildResistor(def, props) {
  const b = new Builder();
  const y = 2.4;
  const body = mat('#d9c08e', { roughness: 0.55 });
  for (const s of [-1, 1]) {
    b.add(cachedGeo('res-cap', () => new THREE.SphereGeometry(1.45, 16, 12)), body, { x: s * 2.35, y, z: 0, sx: 0.85 });
    b.leg([[s * 3.2, y, 0], [s * 4.3, y, 0], [s * 2 * PITCH, y - 0.9, 0], [s * 2 * PITCH, 0, 0]], LEG_R, M.tin);
  }
  b.build();
  const mid = new THREE.MeshStandardMaterial({ map: bandTexture(props.value), roughness: 0.55 });
  const cylinder = new THREE.Mesh(cachedGeo('res-mid', () => new THREE.CylinderGeometry(1.22, 1.22, 4.4, 20, 1, true)), mid);
  cylinder.rotation.z = Math.PI / 2;
  cylinder.position.y = y;
  cylinder.castShadow = true;
  b.root.add(cylinder);
  return {
    root: b.root,
    setProps(p) {
      mid.map = bandTexture(p.value);
      mid.needsUpdate = true;
    },
  };
}

// Botón pulsador -----------------------------------------------------------------------------------
export function buildPushbutton(def, props) {
  const b = new Builder();
  const base = 1.0;
  b.box(6, 3.3, 6, mat('#1f1f22', { roughness: 0.5 }), 0, base, 0);
  b.box(6.1, 0.3, 6.1, M.steel, 0, base + 3.3, 0);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.cyl(0.55, 0.55, 0.5, M.black, sx * 2.3, base + 3.6, sz * 2.3, 10);
  b.cyl(1.9, 1.9, 0.7, M.black, 0, base + 3.6, 0, 20);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      b.leg([[sx * PITCH, base + 1.2, sz * 3.0], [sx * PITCH, base + 0.6, sz * 3.81], [sx * PITCH, 0, sz * 3.81]], 0.35, M.tin);
    }
  }
  b.build();
  const color = (BUTTON_COLORS[props.color] || BUTTON_COLORS.red).hex;
  const capMat = mat(color, { roughness: 0.38 });
  const cap = new THREE.Mesh(cachedGeo('btn-cap', () => new THREE.CylinderGeometry(2.6, 2.75, 2.4, 28)), capMat);
  const upY = base + 4.3 + 1.2;
  cap.position.y = upY;
  cap.castShadow = true;
  cap.userData.interactive = { kind: 'press', key: 'pressed', label: 'Presionar' };
  b.root.add(cap);
  return {
    root: b.root,
    update(v) {
      cap.position.y = upY - (v.pressed ? 0.7 : 0);
    },
    press(key, down) {
      cap.position.y = upY - (down ? 0.7 : 0);
    },
    reset() {
      cap.position.y = upY;
    },
  };
}

// Potenciómetro rotatorio ------------------------------------------------------------------------
let knobTex = null;
function knobTexture() {
  if (knobTex) return knobTex;
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 16;
  const g = c.getContext('2d');
  g.fillStyle = '#202124';
  g.fillRect(0, 0, 128, 16);
  g.fillStyle = '#3a3b40';
  for (let i = 0; i < 32; i++) g.fillRect(i * 4, 0, 2, 16);
  knobTex = texture(c, { aniso: 4 });
  knobTex.wrapS = THREE.RepeatWrapping;
  knobTex.userData.shared = true;
  return knobTex;
}

export function buildPotentiometer(def, props) {
  const b = new Builder();
  const zc = -5.5;
  b.rbox(9.8, 5.2, 9.8, 0.6, mat('#2350b3', { roughness: 0.5 }), 0, 1.6, zc);
  b.cyl(3.4, 3.4, 1.4, M.steel, 0, 6.8, zc, 24);
  for (const x of [-PITCH, 0, PITCH]) b.leg([[x, 2.2, zc + 4.2], [x, 1.6, 0], [x, 0, 0]], 0.38, M.tin);
  b.build();
  const knob = new THREE.Group();
  knob.position.set(0, 8.2, zc);
  const sides = new THREE.MeshStandardMaterial({ map: knobTexture(), roughness: 0.55 });
  const topM = mat('#26272b', { roughness: 0.5 });
  const k = new THREE.Mesh(cachedGeo('knob', () => new THREE.CylinderGeometry(4.4, 4.7, 6, 32)), [sides, topM, topM]);
  k.position.y = 3;
  k.castShadow = true;
  knob.add(k);
  const marker = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.2, 3.6), mat('#f5f5f5', { roughness: 0.4 }));
  marker.position.set(0, 6.05, -2.1);
  knob.add(marker);
  k.userData.interactive = marker.userData.interactive = { kind: 'knob', key: 'value', label: 'Girar' };
  b.root.add(knob);
  const set = (v) => {
    knob.rotation.y = -((v ?? 0.5) - 0.5) * (Math.PI * 1.5);
  };
  set(props.value);
  return {
    root: b.root,
    update(v) {
      if (v.value !== undefined) set(v.value);
    },
    setProps(p) {
      set(p.value);
    },
  };
}

export function buildSlidePotentiometer(def, props) {
  const b = new Builder();
  b.box(60, 2.2, 9, mat('#1c3a6e', { roughness: 0.5 }), 0, 0, 0);
  b.box(58, 6, 8, M.metal, 0, 2.2, 0);
  b.box(46, 0.4, 1.4, M.hole, 0, 8.2, 0);
  for (const [i, z] of [[0, -PITCH], [1, 0], [2, PITCH]]) {
    b.box(1.2, 1.2, 0.64, M.gold, -30.6, 4.5 - 0.6, z);
    void i;
  }
  b.box(3, 2.5, 8, M.blackPlastic, -29.5, 3.2, 0);
  b.build();
  const knob = new THREE.Group();
  const kb = new THREE.Mesh(new THREE.BoxGeometry(4, 6, 9), mat('#2a2b2f', { roughness: 0.5 }));
  kb.position.y = 11;
  kb.castShadow = true;
  const kt = new THREE.Mesh(new THREE.BoxGeometry(4.2, 0.4, 9.2), mat('#f2f2f2', { roughness: 0.4 }));
  kt.position.y = 14.1;
  knob.add(kb, kt);
  kb.userData.interactive = kt.userData.interactive = { kind: 'slider', key: 'value', axis: 'x', range: 46, label: 'Deslizar' };
  b.root.add(knob);
  const set = (v) => {
    knob.position.x = ((v ?? 0.5) - 0.5) * 46;
  };
  set(props.value);
  return {
    root: b.root,
    update(v) {
      if (v.value !== undefined) set(v.value);
    },
    setProps(p) {
      set(p.value);
    },
  };
}

// Interruptor deslizante ---------------------------------------------------------------------------
export function buildSlideSwitch(def, props) {
  const b = new Builder();
  b.box(8.6, 3.8, 4.4, M.blackPlastic, 0, 1.8, 0);
  b.box(9.6, 0.3, 4.8, M.steel, 0, 5.6, 0);
  b.box(5.2, 0.32, 2.0, M.hole, 0, 5.9, 0);
  for (const x of [-PITCH, 0, PITCH]) b.leg([[x, 2.0, 0], [x, 0, 0]], 0.35, M.tin);
  b.build();
  const knob = new THREE.Mesh(new THREE.BoxGeometry(1.8, 2.6, 1.6), mat('#efefea', { roughness: 0.5 }));
  knob.position.y = 6.9;
  knob.castShadow = true;
  knob.userData.interactive = { kind: 'toggle', key: 'toggle', label: 'Cambiar' };
  b.root.add(knob);
  const set = (pos) => {
    knob.position.x = pos === 'right' ? 1.4 : -1.4;
  };
  set(props.position);
  return {
    root: b.root,
    update(v) {
      set(v.position);
    },
    setProps(p) {
      set(p.position);
    },
  };
}

// Zumbador -------------------------------------------------------------------------------------------
let buzzerTex = null;
export function buildBuzzer(def, props) {
  const b = new Builder();
  const body = mat('#1b1b1d', { roughness: 0.45 });
  b.cyl(6, 6, 7.4, body, 0, 1.2, 0, 40);
  b.cyl(5.2, 5.4, 0.4, mat('#151517', { roughness: 0.6 }), 0, 8.6, 0, 40);
  b.cyl(1.1, 1.1, 0.5, M.hole, 0, 8.7, 0, 20);
  for (const x of [-1.5 * PITCH, 1.5 * PITCH]) b.leg([[x, 1.4, 0], [x, 0, 0]], 0.3, M.tin);
  b.build();
  if (!buzzerTex) {
    const { c, g } = canvas(12, 12, 20);
    text(g, '+', 9.2, 6, { size: 3, color: '#e9e9e9', weight: 700 });
    buzzerTex = texture(c);
    buzzerTex.userData.shared = true;
  }
  const label = new THREE.Mesh(new THREE.PlaneGeometry(12, 12).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: buzzerTex, transparent: true, depthWrite: false }));
  label.position.y = 8.62;
  b.root.add(label);
  const ring = new THREE.Mesh(new THREE.RingGeometry(6.5, 7.2, 40).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: '#ffd166', transparent: true, opacity: 0, depthWrite: false }));
  ring.position.y = 9;
  ring.userData.noPick = true;
  b.root.add(ring);
  let t = 0;
  let on = false;
  return {
    root: b.root,
    update(v) {
      on = v.freq > 0 && v.volume > 0;
    },
    animate(dt) {
      if (!on && ring.material.opacity <= 0) return false;
      t = (t + dt * 2.5) % 1;
      const s = on ? 1 + t * 0.9 : 1;
      ring.scale.set(s, 1, s);
      ring.material.opacity = on ? 0.55 * (1 - t) : Math.max(0, ring.material.opacity - dt * 3);
      return true;
    },
    reset() {
      on = false;
      ring.material.opacity = 0;
    },
  };
}

// Texturas dibujadas una vez (lienzo en mm con s px/mm) y compartidas entre piezas iguales.
const texCache = new Map();
function cachedTexture(key, w, h, s, draw) {
  let t = texCache.get(key);
  if (!t) {
    const { c, g } = canvas(w, h, s);
    draw(g, w, h);
    t = texture(c, { aniso: 4 });
    t.userData.shared = true;
    texCache.set(key, t);
  }
  return t;
}

// Capacitor cerámico ------------------------------------------------------------------------------------
// Lenteja de cerámica con recubrimiento de epoxi, de pie entre dos patas a 2.54 mm, con el código al frente.
export function buildCeramicCap(def, props) {
  const f = ceramicFarads(props);
  const code = ceramicCode(f);
  const r = f <= 1e-9 ? 2.1 : f <= 47e-9 ? 2.6 : 3.2;
  const yc = 3.4 + r;
  const b = new Builder();
  const body = mat('#c67a2e', { roughness: 0.55 });
  for (const s of [-1, 1]) {
    const x = (s * PITCH) / 2;
    b.leg([[s * 0.95, yc - r * 0.7, 0], [s * 1.05, 2.6, 0], [x, 1.6, 0], [x, 0, 0]], LEG_R, M.tin);
    // gota del recubrimiento que baja por cada pata
    b.add(cachedGeo('bas:cap-drip', () => new THREE.SphereGeometry(0.5, 10, 8)), body, { x: s * 0.98, y: yc - r * 0.82, sy: 1.8 });
  }
  b.add(cachedGeo('bas:cap-disc', () => new THREE.SphereGeometry(1, 32, 20)), body, { y: yc, sx: r, sy: r, sz: 1.25 });
  b.build();
  const tex = cachedTexture(`bas:cer-${code}`, 4, 2, 64, (g, w, h) => text(g, code, w / 2, h / 2 + 0.05, { size: 1.45, color: '#2a1a0c', weight: 700 }));
  const w = r * 1.25;
  const label = new THREE.Mesh(new THREE.PlaneGeometry(w, w / 2), new THREE.MeshStandardMaterial({ map: tex, transparent: true, roughness: 0.55, depthWrite: false }));
  label.position.set(0, yc + 0.1, 1.27);
  b.root.add(label);
  return { root: b.root };
}

// Capacitor electrolítico ---------------------------------------------------------------------------------
// Lata de aluminio con funda impresa (franja del − hacia la pata −), tapa con ranura de seguridad y patas que
// bajan desde la separación real (2–7.5 mm según el diámetro) hasta agujeros vecinos; la + tiene un codo.
const ECAP_SLEEVE = '#20418c';
const ECAP_STRIPE = '#c9d2e3';

function sleeveTexture(e) {
  const w = Math.PI * e.d;
  return cachedTexture(`bas:ecap-${e.uf}-${e.volts}-${e.d}-${e.h}`, w, e.h, 20, (g) => {
    g.fillStyle = ECAP_SLEEVE;
    g.fillRect(0, 0, w, e.h);
    // Con thetaStart = π la costura queda atrás: u = 0.25 mira a −X (pata −) y u = 0.5 al frente.
    const sw = w * 0.17;
    g.fillStyle = ECAP_STRIPE;
    g.fillRect(w * 0.25 - sw / 2, 0, sw, e.h);
    g.fillStyle = ECAP_SLEEVE;
    for (let y = 1.6; y < e.h - 1; y += 2.4) g.fillRect(w * 0.25 - sw * 0.22, y, sw * 0.44, 0.45);
    // Ranura de sujeción cerca de la tapa
    g.fillStyle = 'rgba(0,0,0,0.35)';
    g.fillRect(0, 0.9, w, 0.35);
    const size = Math.min(1.5, e.d * 0.26);
    text(g, `${e.uf}µF`, w * 0.5, e.h * 0.42, { size, color: '#eef1f7', weight: 700 });
    text(g, `${e.volts}V`, w * 0.5, e.h * 0.42 + size * 1.25, { size: size * 0.9, color: '#eef1f7', weight: 700 });
  });
}

function ventTexture(d) {
  return cachedTexture(`bas:ecap-vent-${d}`, d, d, 24, (g) => {
    g.fillStyle = '#c3c7cd';
    g.fillRect(0, 0, d, d);
    if (d < 6) return;
    g.strokeStyle = '#8d929a';
    g.lineWidth = 0.35;
    g.beginPath();
    g.moveTo(d * 0.22, d / 2);
    g.lineTo(d * 0.78, d / 2);
    g.moveTo(d / 2, d * 0.22);
    g.lineTo(d / 2, d * 0.78);
    g.stroke();
  });
}

export function buildElectrolyticCap(def, props) {
  const e = electrolytic(props);
  const r = e.d / 2;
  const y0 = 1.2; // la lata queda un poco arriba de la protoboard
  const span = e.d <= 5 ? 1 : e.d <= 6.3 ? 1.25 : e.d <= 8 ? 1.75 : e.d <= 13 ? 2.5 : 3.75;
  const b = new Builder();
  b.leg([[-span, y0, 0], [-span, y0 - 0.3, 0], [-PITCH / 2, 0.5, 0], [-PITCH / 2, 0, 0]], LEG_R, M.tin);
  b.leg([[span, y0, 0], [span, y0 - 0.3, 0], [PITCH / 2, 0.8, 0], [PITCH / 2 + 0.45, 0.5, 0], [PITCH / 2, 0.2, 0], [PITCH / 2, 0, 0]], LEG_R, M.tin);
  b.cyl(r * 0.97, r * 0.97, 0.5, mat('#1a1a1d', { roughness: 0.85 }), 0, y0 - 0.2, 0, 28);
  b.build();
  const side = new THREE.MeshStandardMaterial({ map: sleeveTexture(e), roughness: 0.38 });
  const top = new THREE.MeshStandardMaterial({ map: ventTexture(e.d), roughness: 0.3, metalness: 0.75 });
  const bottom = mat('#1a1a1d', { roughness: 0.85 });
  const can = new THREE.Mesh(cachedGeo(`bas:ecap${r},${e.h}`, () => new THREE.CylinderGeometry(r, r, e.h, 36, 1, false, Math.PI)), [side, top, bottom]);
  can.position.y = y0 + e.h / 2;
  can.castShadow = true;
  b.root.add(can);
  return { root: b.root };
}

// Temporizador NE555 (DIP-8) -----------------------------------------------------------------------------
export function buildNe555(def, props) {
  const b = new Builder();
  const len = 4 * PITCH - 0.6;
  b.box(len, 3.3, 6.35, M.chip, 0, 0.9, 0);
  for (const p of getPins(def.type, props)) {
    const s = Math.sign(p.z);
    b.box(0.55, 0.28, 0.9, M.tin, p.x, 1.85, s * 3.4);
    b.box(1.1, 1.0, 0.28, M.tin, p.x, 1.0, s * 3.81);
    b.box(0.5, 1.2, 0.26, M.tin, p.x, 0, s * 3.81);
  }
  b.build();
  const tex = cachedTexture('bas:ne555', len, 6.35, 30, (g, w, d) => {
    g.fillStyle = '#1b1b1e';
    g.fillRect(0, 0, w, d);
    g.fillStyle = '#0f0f11';
    g.beginPath();
    g.arc(0, d / 2, 0.9, -Math.PI / 2, Math.PI / 2);
    g.fill();
    g.beginPath();
    g.arc(1.5, d - 1.2, 0.42, 0, Math.PI * 2);
    g.fill();
    text(g, 'NE555P', w / 2 + 0.6, d / 2 - 0.6, { size: 1.5, color: '#acaeb2', weight: 600 });
    text(g, 'TI  2AM  K4', w / 2 + 0.6, d / 2 + 1.3, { size: 0.9, color: '#8c8e92', weight: 500 });
  });
  const top = new THREE.Mesh(new THREE.PlaneGeometry(len, 6.35), new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55 }));
  top.rotation.x = -Math.PI / 2;
  top.position.y = 4.21;
  b.root.add(top);
  return { root: b.root };
}

// Placa táctil -------------------------------------------------------------------------------------------
// Cuadro de papel aluminio arrugado, cable blanco pegado con cinta y la punta estañada donde va el cable.
function foilTexture() {
  return cachedTexture('bas:foil', PLATE.W, PLATE.D, 8, (g, w, d) => {
    g.fillStyle = '#c4c8cd';
    g.fillRect(0, 0, w, d);
    let seed = 7;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    // Facetas de las arrugas: polígonos claros y oscuros
    for (let i = 0; i < 260; i++) {
      const x = rnd() * w;
      const y = rnd() * d;
      const k = Math.round(150 + rnd() * 90);
      g.fillStyle = `rgba(${k},${k + 3},${k + 7},0.55)`;
      g.beginPath();
      g.moveTo(x, y);
      for (let j = 0; j < 3; j++) g.lineTo(x + (rnd() - 0.5) * 9, y + (rnd() - 0.5) * 9);
      g.closePath();
      g.fill();
    }
    g.strokeStyle = 'rgba(110,115,122,0.2)';
    g.lineWidth = 0.15;
    for (let i = 0; i < 24; i++) {
      g.beginPath();
      let x = rnd() * w;
      let y = rnd() * d;
      g.moveTo(x, y);
      for (let j = 0; j < 4; j++) g.lineTo((x += (rnd() - 0.5) * 12), (y += (rnd() - 0.5) * 12));
      g.stroke();
    }
  });
}

export function buildTouchPlate(def, props) {
  const b = new Builder();
  const x0 = -PLATE.W / 2;
  const tipX = PLATE.PIN_X;
  const wire = new THREE.CatmullRomCurve3([[x0 + 7, 0.5, 4], [x0 + 1, 0.9, 3], [x0 - 9, 1.1, -3], [tipX + 9, 1.0, 1], [tipX + 3.2, 1.0, 0]].map((p) => new THREE.Vector3(...p)), false, 'centripetal', 0.5);
  b.add(new THREE.TubeGeometry(wire, 40, 0.8, 8, false), mat('#e6e6e2', { roughness: 0.5 }));
  b.cylX(0.36, 3.4, M.tin, tipX + 1.7, 1.0, 0, 10);
  b.cylX(0.3, 6, M.tin, x0 + 10, 0.32, 4, 8);
  b.build();
  const foilMat = new THREE.MeshStandardMaterial({ map: foilTexture(), roughness: 0.33, metalness: 0.85 });
  const edge = mat('#b7bbc1', { roughness: 0.4, metalness: 0.8 });
  const foil = new THREE.Mesh(new THREE.BoxGeometry(PLATE.W, 0.12, PLATE.D), [edge, edge, foilMat, edge, edge, edge]);
  foil.position.y = 0.06;
  foil.receiveShadow = true;
  foil.userData.interactive = { kind: 'press', key: 'touched', label: 'Tocar' };
  b.root.add(foil);
  // Cinta adhesiva que sujeta el cable a la lámina
  const tape = new THREE.Mesh(new THREE.BoxGeometry(9, 0.1, 7), new THREE.MeshStandardMaterial({ color: '#efe6c8', roughness: 0.6, transparent: true, opacity: 0.55, depthWrite: false }));
  tape.position.set(x0 + 9, 0.85, 4);
  tape.userData.noPick = true;
  b.root.add(tape);
  // Yema del dedo, solo mientras se toca
  const finger = new THREE.Mesh(cachedGeo('bas:finger', () => new THREE.CapsuleGeometry(6.5, 24, 6, 18)), mat('#e0a98a', { roughness: 0.7 }));
  // Inclinado hacia +X: el punto más bajo de la yema queda apenas sobre la lámina.
  finger.rotation.set(0, 0, -0.55);
  finger.position.set(8.5, 6.5 + 12 * Math.cos(0.55) + 0.18, 2);
  finger.userData.noPick = true;
  finger.visible = false;
  b.root.add(finger);
  const show = (on) => {
    finger.visible = on;
  };
  return {
    root: b.root,
    update(v) {
      show(!!v.touched);
    },
    press(key, down) {
      show(down);
    },
    reset() {
      show(false);
    },
  };
}

export const MODELS = {
  led: buildLed,
  'rgb-led': buildRgbLed,
  resistor: buildResistor,
  pushbutton: buildPushbutton,
  potentiometer: buildPotentiometer,
  'slide-potentiometer': buildSlidePotentiometer,
  'slide-switch': buildSlideSwitch,
  buzzer: buildBuzzer,
  'capacitor-ceramic': buildCeramicCap,
  'capacitor-electrolytic': buildElectrolyticCap,
  ne555: buildNe555,
  'touch-plate': buildTouchPlate,
};
